import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { strings } from '/imports/strings';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Select } from '/imports/ui/Select';
import { Page } from '/imports/ui/Page';
import { BuildingTypeOptions, InstallTypeOptions, Project, enrichProject, essCapacityOptions, modelClass, twoHourBatteryMap } from '/imports/api/projects';
import { objectKeys } from '/imports/utils';
import { AddressInput } from '/imports/ui/AddressInput';
import { useFindOne, useMethod, useUser, useIsLuminia } from '/imports/hooks';
import { GmapsAddress, sanDiegoCoords, useGoogleMap } from '/imports/gmaps';
import { FieldSpec } from '/imports/ui/FormBuilder';
import { NumberInput } from '/imports/ui/NumberInput';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Orgs } from '/imports/api/orgs';

const ProjectPageStyled = styled(Page)`
  .body-wrap {
    display: flex;
    gap: 1rem;
    > div {
      flex: 1;
    }
    .map-wrap {
      height: 61.5rem;
    }
  }
`;

const ModelFormStyled = styled(Page)`
  .page-heading {
    display: none;
  }
  .page-body {
    padding: 0;
    overflow: visible;
  }
  .delete {
    display: none;
  }
  .buttons {
    flex-direction: row-reverse;
  }
  .section-title {
    padding: 0;
    margin: 0;
    margin-bottom: -1.5rem;
    font-weight: normal;
  }
`;

/** Create/edit form for a Project, with a live map preview of the entered address. */
export const ProjectPage = () => {
  const { _id } = useParams() as { _id: string };
  const isNew = _id === 'new';
  const navigate = useNavigate();
  const user = useUser();
  const isLuminia = useIsLuminia();
  const orgId = user?.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  const { isLoaded } = useGoogleMap();
  // The ITC adder defaults live here AND in projectMethods.ts's $set fallback, and the two must agree: this
  // form sends explicit values, so the server's `=== undefined` default never sees a new project and this
  // is the default that actually takes effect. Energy-community ON, domestic-content OFF: 30% base plus
  // one 10% adder reproduces the flat 40% the pre-v1.12 CEA model applied, so new projects price as the
  // existing ones do. (Briefly all-off on 2026-09-29 at QA's request; reverted the same day — Paul's call.)
  const defaultUpdate: Partial<Project> = isNew ?
    {
      _id, active: true, proposalType: 'essSolar', ownershipType: 'tenant', ptoYear: 2027,
      solarEpcItcEligible: true, solarEnergyCommunityAdder: true, solarDomesticContentAdder: false,
      storageEpcItcEligible: true, storageEnergyCommunityAdder: true, storageDomesticContentAdder: false,
      siteLeasePayment: 'Full'
    }
    : { _id };
  const [update, setUpdate] = useState<Partial<Project>>(defaultUpdate);
  const [project] = useMethod(modelClass.getMethod, _id, [_id], () => !isNew);
  const documentRef = useRef<Project>(undefined);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const modelFormProps = { styled: ModelFormStyled, modelClass, setUpdate, update, documentRef, defaultUpdate, formDeps: [update, showAdditionalFields] };
  const [address, setAddress] = useState<GmapsAddress>();
  useEffect(() => {
    if (!project?.address) return;
    setAddress(project.address);
  }, [project]);
  useEffect(() => setUpdate({ ...defaultUpdate, ...update, address }), [address]);
  const Form = <ModelFormPage
    {...modelFormProps}
    formSpec={() => {
      let rows: FieldSpec<Project>[][] = [
        [
          { field: 'name', asterisk: true }
        ],
        [
          { field: 'propertyDetails', input: () => <h3 className="section-title">{strings.PropertyDetails()}</h3> }
        ],
        [{
          field: 'address', asterisk: true,
          input: { type: AddressInput, props: { autoCompleteRequest: { locationBias: sanDiegoCoords }, onChange: (address: GmapsAddress) => setAddress(address) } }
        }],
        [
          { field: 'buildingTypes', label: strings.BuildingType(), input: { type: Select, props: { options: [{ value: '', label: '' }, ...BuildingTypeOptions], onChange: (option: string) => setUpdate({ ...update, buildingTypes: [option] }) } } },
          {
            field: 'annualConsumption', label: `${strings.AnnualConsumption()} (kWh)`, tip: strings.ProjectAnnualConsumptionTip(),
            input: { type: NumberInput, props: { placeholder: `${strings.Example()} 750000` } }
          },
        ]
      ];
      const canShowAdditionalFields = org?.projectChannel === 'dealer' || isLuminia;
      if (showAdditionalFields && canShowAdditionalFields) {
        rows = [
          ...rows,
          [
            { field: 'solarInputs', input: () => <h3 className="section-title">{strings.SolarInputs()}</h3> }
          ],
          [
            {
              field: 'systemSize', label: `${strings.SystemSize()} (kW)`, tip: strings.ProjectSystemSizeTip(),
              input: { type: NumberInput, props: { placeholder: `${strings.Example()} 500` } }
            },
            {
              field: 'annualProduction', label: `${strings.AnnualProduction()} (kWh)`, tip: strings.ProjectAnnualProductionTip(),
              input: { type: NumberInput, props: { placeholder: `${strings.Example()} 750000` } }
            },

          ],
          [
            {
              field: 'epcPrice', label: `${strings.EpcPrice()} ($/W)`, tip: strings.ProjectEpcPriceTip(),
              input: { type: NumberInput, props: { placeholder: `${strings.Example()} 1.5` } }
            },
            {
              field: 'avoidedCost', label: `${strings.AvoidedCost()} ($/kWh)`, tip: strings.ProjectAvoidedCostTip(),
              input: { type: NumberInput, props: { placeholder: `${strings.Example()} 0.14` } }
            }
          ],
          [
            { field: 'installType', input: { type: Select, props: { options: [{ value: '', label: '' }, ...InstallTypeOptions] } } }
          ],
          [
            { field: 'essInputs', input: () => <h3 className="section-title">{strings.EnergyStorageInputs()}</h3> }
          ],
          [
            {
              field: 'essSize', label: `${strings.EssSize()} (kW)`, tip: strings.ProjectEssSizeTip(),
              input: {
                type: Select,
                props: {
                  value: `${update.essSize || ''}`,
                  onChange: (v: string) => {
                    const essSize = v ? parseInt(v) : undefined;
                    const essCapacity = essSize ? parseInt(essCapacityOptions(essSize)[0]?.value || '0') || undefined : undefined;
                    setUpdate({ ...update, essSize, essCapacity });
                  },
                  options: [{ value: '', label: '' }, ...objectKeys(twoHourBatteryMap).map((k) => ({ value: k, label: k }))],
                },
              },
            },
            {
              field: 'essCapacity', label: `${strings.EssCapacity()} (kWh)`, tip: strings.ProjectEssCapacityTip(),
              input: {
                type: Select,
                props: {
                  value: `${update.essCapacity || ''}`,
                  onChange: (v: string) => setUpdate({ ...update, essCapacity: v ? parseInt(v) : undefined }),
                  disabled: !update.essSize,
                  options: essCapacityOptions(update.essSize),
                },
              },
            },

          ],
          [
            {
              field: 'essEpcPrice', label: `${strings.EssEpcPrice()} ($/kWh)`, tip: strings.ProjectEssEpcPriceTip(),
              input: { type: NumberInput, props: { placeholder: `${strings.Example()} 800` } }
            },
            {
              field: 'essAvoidedCost', label: `${strings.EssAvoidedCost()} ($/kWh)`, tip: strings.ProjectEssAvoidedCostTip(),
              input: { type: NumberInput, props: { placeholder: `${strings.Example()} 150` } }
            }
          ],
          [
            { field: 'additionalInputs', input: () => <a href="#" onClick={() => setShowAdditionalFields(false)}>—{strings.AdditionalInputs()}</a> }
          ],
        ];
      }
      else if (canShowAdditionalFields) {
        rows = [
          ...rows,
          [
            { field: 'additionalInputs', input: () => <a href="#" onClick={() => setShowAdditionalFields(true)}>+{strings.AdditionalInputs()}</a> }
          ],
        ];
      }
      return rows;
    }}
    beforeSave={() => { update._skipValidation = false; return true; }}
    redirect={null}
    onSave={async (_id) => {
      const enrichableFields: (keyof Project)[] = [
        'buildingTypes', 'annualConsumption', 'systemSize', 'annualProduction',
        'epcPrice', 'avoidedCost', 'installType', 'essCapacity', 'essSize',
        'essEpcPrice', 'essAvoidedCost', 'rooftopArea', 'utilities', 'ccas', 'county',
      ];
      const manuallyEntered = enrichableFields.filter((f) => {
        const v = update[f];
        return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
      }) as string[];
      await enrichProject.call({ _id, manuallyEntered });
      navigate(`/projects/${_id}/view`);
    }}
  />;
  if (!isLoaded) return <LoadingFlex />;
  return (
    <ProjectPageStyled>
      <PageHeading
        left={<h1>{project ? `${strings.Edit()} ${project.name}` : strings.NewProject()}</h1>}
      />
      <PageBody>
        <div className="body-wrap">
          <div>{Form}</div>
          <div className="map-wrap">
            <GoogleMap
              mapContainerStyle={{ width: '100%', minHeight: '61.5rem' }} center={address?.location || sanDiegoCoords} zoom={address?.location ? 19 : 3} mapTypeId="hybrid"
            >
              {
                address ? <Marker position={address.location} /> : null
              }
            </GoogleMap>
          </div>
        </div>
      </PageBody>
    </ProjectPageStyled>
  );
};